import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Alert, Table, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronUp, 
  faChevronDown,
  faMoneyBill,
  faHome,
  faCar,
  faUtensils,
  faShoppingCart,
  faStethoscope,
  faDumbbell,
  faGraduationCap,
  faPlug,
  faWifi,
  faMobileAlt,
  faTv,
  faPlane,
  faPaw,
  faGift,
  faPiggyBank,
  faCreditCard,
  faHammer,
  faTshirt,
  faBus,
  faFutbol,
  faLaptop,
  faSeedling,
  faTools,
  faPaintBrush,
  faEdit,
  faTrash,
  faCheck,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { getRelatedBills } from '../../services/RequirementService';
import { formatDate, formatCurrency } from '../../utils/formatters';
import EditBillModal from './EditBillModal';
import { deleteBill } from '../../services/BillService';

// Map of category icons to FontAwesome icons
const iconMap = {
  'home': faHome,
  'car': faCar,
  'utensils': faUtensils,
  'shopping-cart': faShoppingCart,
  'medical': faStethoscope,
  'dumbbell': faDumbbell,
  'graduation-cap': faGraduationCap,
  'plug': faPlug,
  'wifi': faWifi,
  'mobile-alt': faMobileAlt,
  'tv': faTv,
  'plane': faPlane,
  'paw': faPaw,
  'gift': faGift,
  'piggy-bank': faPiggyBank,
  'credit-card': faCreditCard,
  'money-bill': faMoneyBill,
  'hammer': faHammer,
  'tshirt': faTshirt,
  'bus': faBus,
  'laptop': faLaptop,
  'seedling': faSeedling,
  'tools': faTools,
  'paint-brush': faPaintBrush
};

const RelatedBillsModal = ({ 
  show, 
  onHide, 
  requirementId, 
  requirementName,
  categoryName,
  categoryIcon,
  categoryColor
}) => {
  const [bills, setBills] = useState([]);
  const [buffer, setBuffer] = useState({ before: [], after: [] });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [deletingBill, setDeletingBill] = useState(null);
  const [hasMorePast, setHasMorePast] = useState(true);
  const [hasMoreFuture, setHasMoreFuture] = useState(true);
  const WINDOW_SIZE = 5;
  const BUFFER_SIZE = 3;

  const fetchBills = async (direction = 'both', afterDate = null, beforeDate = null) => {
    if (!requirementId) return;
    
    setLoading(true);
    try {
      const data = await getRelatedBills(requirementId, { 
        direction,
        after_date: afterDate,
        before_date: beforeDate
      });
      
      // Process the response based on direction
      if (direction === 'both') {
        // Initial load - use the window and buffers directly from the backend
        const windowBills = data.window_bills || [];
        windowBills.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
        setBills(windowBills);
        
        const beforeBuffer = (data.before_buffer || []).sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
        const afterBuffer = (data.after_buffer || []).sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
        
        setBuffer({
          before: beforeBuffer,
          after: afterBuffer
        });
        setCurrentIndex(0);
        setHasMorePast(data.has_more_past || false);
        setHasMoreFuture(data.has_more_future || false);
      } else if (direction === 'future' && data?.window_bills?.length > 0) {
        // For future bills, update the buffer with the new bills
        const newBills = data.window_bills.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
        setBuffer(prev => ({
          ...prev,
          after: [...prev.after, ...newBills].slice(0, BUFFER_SIZE)
        }));
        setHasMoreFuture(data.has_more_future || false);
      } else if (direction === 'past' && data?.window_bills?.length > 0) {
        // For past bills, update the buffer with the new bills
        // Only add bills that are not already in the current window or buffer
        const currentBillDates = new Set([...bills, ...buffer.before].map(bill => bill.due_date));
        const newBills = data.window_bills
          .filter(bill => !currentBillDates.has(bill.due_date))
          .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
        
        if (newBills.length > 0) {
          setBuffer(prev => ({
            before: [...newBills, ...prev.before].slice(-BUFFER_SIZE),
            after: prev.after
          }));
        }
        setHasMorePast(data.has_more_past || false);
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to fetch bills. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (buffer.before.length > 0) {
      // Take the most recent bill from the before buffer and add it to the start of the window
      const newBill = buffer.before[buffer.before.length - 1];
      const newBills = [newBill, ...bills.slice(0, -1)];
      
      // Ensure the bills are in chronological order
      newBills.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
      
      setBills(newBills);
      setBuffer(prev => ({
        before: prev.before.slice(0, -1),
        after: [...prev.after, bills[bills.length - 1]]
      }));
    } else if (hasMorePast) {
      // We need to load more past bills
      const firstBill = bills[0];
      if (firstBill?.due_date) {
        fetchBills('past', null, firstBill.due_date);
      }
    }
  };

  const handleNext = () => {
    if (buffer.after.length > 0) {
      const newBills = [...bills.slice(1), buffer.after[0]];
      setBills(newBills);
      setBuffer(prev => ({
        before: [...prev.before, bills[0]],
        after: prev.after.slice(1)
      }));
    } else if (hasMoreFuture) {
      // We need to load more future bills
      const lastBill = bills[bills.length - 1];
      if (lastBill?.due_date) {
        fetchBills('future', lastBill.due_date, null);
      }
    }
  };

  const handleEditBill = (bill) => {
    setSelectedBill(bill);
    setShowEditModal(true);
  };

  const handleDeleteBill = async (billId) => {
    setDeletingBill(billId);
    try {
      await deleteBill(billId);
      setBills(bills.filter(bill => bill.id !== billId));
    } catch (error) {
      setError('Failed to delete bill');
    } finally {
      setDeletingBill(null);
    }
  };

  const handleBillUpdateSuccess = () => {
    fetchBills();
  };

  useEffect(() => {
    if (show && requirementId) {
      fetchBills('both');
    }
  }, [show, requirementId]);

  const getBillStyle = (bill) => {
    if (!bill) return {};
    
    const now = new Date();
    const dueDate = new Date(bill.due_date);
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    
    const dayAfterTomorrow = new Date(now);
    dayAfterTomorrow.setDate(now.getDate() + 2);
    
    const isOverdue = dueDate < now;
    const isDueSoon = dueDate.toDateString() === now.toDateString() ||
                     dueDate.toDateString() === tomorrow.toDateString() ||
                     dueDate.toDateString() === dayAfterTomorrow.toDateString();

    if (bill.paid) {
      return {
        backgroundColor: '#e0e0e0',
        color: '#a0a0a0'
      };
    }
    
    if (isOverdue) {
      return { color: '#d9534f' };
    }
    
    if (isDueSoon) {
      return { color: '#f5c542' };
    }

    return {};
  };

  const renderBill = (bill, index) => {
    if (!bill) return null;
    
    const style = getBillStyle(bill);
    const uniqueKey = `${bill.id}-${index}-${bill.due_date}`;
    
    return (
      <tr key={uniqueKey} style={style}>
        <td>{formatDate(bill.due_date)}</td>
        <td>{formatCurrency(bill.amount)}</td>
        <td>
          <Badge bg={bill.paid ? "success" : "warning"}>
            {bill.paid ? "Paid" : "Unpaid"}
          </Badge>
        </td>
        <td>
          <Button
            variant="outline-primary"
            size="sm"
            className="me-2"
            onClick={() => handleEditBill(bill)}
          >
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDeleteBill(bill.id)}
            disabled={deletingBill === bill.id}
          >
            {deletingBill === bill.id ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <FontAwesomeIcon icon={faTrash} />
            )}
          </Button>
        </td>
      </tr>
    );
  };

  const getIcon = (iconName) => {
    return iconMap[iconName] || faMoneyBill;
  };

  const modalStyle = {
    content: {
      border: `2px solid ${categoryColor}`,
      borderRadius: '0.5rem'
    }
  };

  const getLighterColor = (color) => {
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      const lighter = {
        r: Math.round(r * 0.15 + 255 * 0.85),
        g: Math.round(g * 0.15 + 255 * 0.85),
        b: Math.round(b * 0.15 + 255 * 0.85)
      };
      return `rgb(${lighter.r}, ${lighter.g}, ${lighter.b})`;
    }
    return `${color}26`;
  };

  const headerStyle = {
    backgroundColor: getLighterColor(categoryColor),
    margin: 0,
    padding: '1rem',
    borderTopLeftRadius: 'calc(0.5rem - 2px)',
    borderTopRightRadius: 'calc(0.5rem - 2px)'
  };

  return (
    <>
      <Modal 
        show={show} 
        onHide={onHide} 
        size="lg"
        centered
        contentClassName="modal-content-custom"
        style={modalStyle}
      >
        <Modal.Header 
          closeButton
          className="border-bottom-0"
          style={headerStyle}
        >
          <Modal.Title className="w-100">
            <div className="small mb-1" style={{ fontSize: '0.9rem', color: categoryColor }}>
              Related bills for
            </div>
            <div className="d-flex align-items-center">
              <FontAwesomeIcon 
                icon={getIcon(categoryIcon)} 
                className="me-2"
                style={{ color: categoryColor }}
              />
              <span className="h5 mb-0" style={{ color: categoryColor }}>
                {requirementName}
              </span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          {/* Up Arrow Navigation */}
          <div className="d-flex justify-content-center mb-3">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handlePrevious}
              disabled={!hasMorePast && buffer.before.length === 0}
              style={{ 
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FontAwesomeIcon icon={faChevronUp} />
            </Button>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
          
          <Table striped hover>
            <thead>
              <tr>
                <th>Due Date</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="text-center">
                    <Spinner animation="border" />
                  </td>
                </tr>
              ) : bills.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center">No bills found</td>
                </tr>
              ) : (
                bills.map((bill, index) => renderBill(bill, index))
              )}
            </tbody>
          </Table>

          {/* Down Arrow Navigation */}
          <div className="d-flex justify-content-center mt-3">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleNext}
              disabled={!hasMoreFuture && buffer.after.length === 0}
              style={{ 
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FontAwesomeIcon icon={faChevronDown} />
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <EditBillModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        bill={selectedBill}
        onSuccess={handleBillUpdateSuccess}
      />
    </>
  );
};

// Add some CSS to ensure the modal border is visible
const style = document.createElement('style');
style.textContent = `
  .modal-content-custom {
    overflow: hidden;
    border-width: 2px !important;
  }
`;
document.head.appendChild(style);

export default RelatedBillsModal; 